<template>
    <div class="formWrap">
        <a :href="$page.props.home_url ?? '/'" class="formWrap_logo">
            <IconLogo />
        </a>
        <div v-show="formView === 'organization'" key="organization" class="formWrap_container">
            <h1>Business info</h1>
            <CompanyRegisterForm
                :countries="countries"
                :industries="industries"
                @change="handleOrganizationChange"
            />
        </div>
        <div v-show="formView === 'plan'" key="plan">
            <h1>Choose a plan</h1>
            <ChoosePlanTable
                v-model="formData.coupon"
                :plans="plans"
                @choose="handlePlanChoose"
            />
            <div class="back_button_container">
                <BaseButton color="filled" @click="formView = 'organization'">
                    Go Back
                </BaseButton>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import route from 'ziggy-js';
import { onMounted, reactive, ref } from 'vue';
import { router, usePage } from '@inertiajs/vue2';
import IconLogo from '@/components/icons/IconLogo.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import ChoosePlanTable from '@/components/ChoosePlanTable.vue';
import CompanyRegisterForm from '@/components/CompanyRegisterForm.vue';
import type { SelectOption, OrganizationFormData, Incomplete, SubscriptionPlan } from '../types';

withDefaults(defineProps<{
    plans?: SubscriptionPlan[];
    countries?: SelectOption[];
    industries?: SelectOption[];
}>(), {
    plans: () => [],
    countries: () => [],
    industries: () => [],
});

const formView = ref<'organization' | 'plan'>('organization');
const formData = reactive<Incomplete<OrganizationFormData>>({
    name: null,
    email: null,
    country: null,
    industry: null,
    website: null,
    phone: null,
    coupon: null,
    plan_id: null,
});

function handleOrganizationChange(data: Omit<OrganizationFormData, 'coupon' | 'plan_id'>) {
    formData.name = data.name;
    formData.email = data.email;
    formData.country = data.country;
    formData.industry = data.industry;
    formData.website = data.website;
    formData.phone = data.phone;

    formView.value = 'plan';
}

function handlePlanChoose(planId: OrganizationFormData['plan_id']) {
    formData.plan_id = planId;

    router.post(route('organization.store'), formData, {
        onError: () => {
            formView.value = 'organization';
        },
    });
}

const { props } = usePage();

onMounted(() => {
    formView.value = props.organization ? 'plan' : 'organization';
});
</script>

<style lang="scss">
.formWrap {
    padding: 32px;

    @media screen and (max-width: 767px) {
        padding: 16px;
    }

    &_container {
        max-width: 557px;
        margin: 0 auto;
        @media screen and (max-width: 1540px) {
            max-width: 424px;
        }
    }
    &_logo {
        width: 225px;
        margin: 0 auto;
        display: block;
        text-decoration: none;
        svg {
            width: 100%;
            height: auto;
        }

        @media screen and (max-width: 1540px) {
            width: 170px;
        }
    }

    h1 {
        text-align: center;
        padding: 28px 0 36px;
        @media screen and (max-width: 1540px) {
            padding: 23px 0 27px;
        }
    }

    &_form_bottom {
        padding-top: 20px;
    }

    .back_button_container {
        display: flex;
        align-items: center;
        justify-content: center;
        padding-top: 20px;
    }
}

/* Enter and leave animations can use different */
/* durations and timing functions.              */
.slide-fade-enter-active, .slide-fade-leave-active {
    transition: all .3s ease;
}

.slide-fade-enter, .slide-fade-leave-to {
    transform: translateX(10px);
    opacity: 0;
}
</style>
